/**
 * ServiceCatalog — /crm/catalog
 * Service & Package Catalog: Admin manages, Sales/Planners browse
 */
import { useState, useEffect, useCallback } from "react";
import {
  Search, Plus, Loader2, ChevronRight, Tag, Package2,
  Edit3, Archive, CheckCircle2, RefreshCw, BookOpen,
} from "lucide-react";
import { serviceCatalogApi } from "@/services/api";
import { hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const C = {
  ivory:   "#FBF7F2",
  sand:    "#FAF6F0",
  gold:    "#C9A15F",
  charcoal:"#3A3330",
  slate:   "#7A706A",
  divider: "rgba(201,161,95,0.15)",
};

const STATUS_CONFIG = {
  DRAFT:     { bg: "#F3F4F6", text: "#374151", label: "Draft" },
  PUBLISHED: { bg: "#D1FAE5", text: "#065F46", label: "Published" },
  ARCHIVED:  { bg: "#FEE2E2", text: "#991B1B", label: "Archived" },
  RETIRED:   { bg: "#F3F4F6", text: "#6B7280", label: "Retired" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  return (
    <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ background: cfg.bg, color: cfg.text }}>
      {cfg.label}
    </span>
  );
}

const fmtCurrency = (v, currency = "INR") =>
  v != null ? `${currency === "INR" ? "₹" : currency} ${Number(v).toLocaleString("en-IN")}` : "—";

// ── Create Service Modal ──────────────────────────────────────────────────────
function CreateServiceModal({ onClose, onSuccess }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    service_code: "",
    service_name: "",
    description: "",
    unit: "EVENT",
    pricing_basis: "FLAT_RATE",
    initial_price_amount: "",
    currency: "INR",
    effective_from: new Date().toISOString().split("T")[0],
  });
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!form.service_code.trim() || !form.service_name.trim()) {
      toast.error("Service code and name are required");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        service_code: form.service_code,
        service_name: form.service_name,
        description: form.description,
        unit: form.unit,
        pricing_basis: form.pricing_basis,
      };
      if (form.initial_price_amount) {
        payload.initial_price = {
          base_price: parseFloat(form.initial_price_amount),
          currency: form.currency,
          effective_from: form.effective_from,
          pricing_basis: form.pricing_basis,
        };
      }
      await serviceCatalogApi.createService(payload);
      toast.success("Service created");
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create service");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Service</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Service Code *</Label>
              <Input data-testid="service-code-input" className="mt-1 h-8 text-sm" placeholder="SVC-001"
                value={form.service_code} onChange={e => setForm(p => ({ ...p, service_code: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Unit</Label>
              <select className="mt-1 w-full border rounded-md px-3 py-1.5 text-sm h-8"
                value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))}>
                {["DAY","HOUR","PERSON","TABLE","EVENT","ITEM","PLATE","SET","SESSION","OTHER"].map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Service Name *</Label>
            <Input data-testid="service-name-input" className="mt-1 h-8 text-sm" placeholder="Photography & Videography"
              value={form.service_name} onChange={e => setForm(p => ({ ...p, service_name: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input className="mt-1 h-8 text-sm" placeholder="Brief description..."
              value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Pricing Basis</Label>
            <select className="mt-1 w-full border rounded-md px-3 py-1.5 text-sm h-8"
              value={form.pricing_basis} onChange={e => setForm(p => ({ ...p, pricing_basis: e.target.value }))}>
              {["PER_UNIT","FLAT_RATE","PER_PERSON","PER_TABLE","TIERED","CUSTOM"].map(b => (
                <option key={b} value={b}>{b.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Initial Price</Label>
              <Input data-testid="service-price-input" type="number" className="mt-1 h-8 text-sm" placeholder="0"
                value={form.initial_price_amount} onChange={e => setForm(p => ({ ...p, initial_price_amount: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Effective From</Label>
              <Input type="date" className="mt-1 h-8 text-sm"
                value={form.effective_from} onChange={e => setForm(p => ({ ...p, effective_from: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="create-service-submit-btn" className="flex-1" style={{ background: C.gold }}
              onClick={handleCreate} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Service
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Create Package Modal ──────────────────────────────────────────────────────
function CreatePackageModal({ services, onClose, onSuccess }) {
  const [form, setForm] = useState({ package_code: "", package_name: "", description: "", currency: "INR", initial_price: "" });
  const [selectedServices, setSelectedServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!form.package_code.trim() || !form.package_name.trim()) { toast.error("Code and name required"); return; }
    setLoading(true);
    try {
      await serviceCatalogApi.createPackage({
        ...form,
        initial_price: form.initial_price ? parseFloat(form.initial_price) : undefined,
        items: selectedServices.map(sid => ({ service_id: sid, quantity: 1, is_optional: false })),
      });
      toast.success("Package created");
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create package");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New Package Bundle</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Package Code *</Label>
              <Input data-testid="package-code-input" className="mt-1 h-8 text-sm" placeholder="PKG-001"
                value={form.package_code} onChange={e => setForm(p => ({ ...p, package_code: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Package Price (optional)</Label>
              <Input type="number" className="mt-1 h-8 text-sm" placeholder="Flat price"
                value={form.initial_price} onChange={e => setForm(p => ({ ...p, initial_price: e.target.value }))} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Package Name *</Label>
            <Input data-testid="package-name-input" className="mt-1 h-8 text-sm" placeholder="Premium Wedding Package"
              value={form.package_name} onChange={e => setForm(p => ({ ...p, package_name: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input className="mt-1 h-8 text-sm" placeholder="What's included..."
              value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Include Services</Label>
              {(() => {
                const pub = services.filter(s => s.status === "PUBLISHED");
                if (!pub.length) return null;
                return (
                  <div className="flex items-center gap-2" data-testid="pkg-service-bulk-controls">
                    {selectedServices.length < pub.length && (
                      <button
                        type="button"
                        data-testid="pkg-services-select-all-btn"
                        onClick={() => setSelectedServices(pub.map(s => s.id))}
                        className="text-[11px] font-medium"
                        style={{ color: "#0176D3" }}
                      >
                        Select All
                      </button>
                    )}
                    {selectedServices.length > 0 && (
                      <button
                        type="button"
                        data-testid="pkg-services-clear-btn"
                        onClick={() => setSelectedServices([])}
                        className="text-[11px] font-medium"
                        style={{ color: "#706E6B" }}
                      >
                        Clear
                      </button>
                    )}
                    <span className="text-[10px]" style={{ color: "#706E6B" }}>
                      {selectedServices.length} of {pub.length}
                    </span>
                  </div>
                );
              })()}
            </div>
            <div className="mt-1 border rounded-md p-2 max-h-32 overflow-y-auto space-y-1">
              {services.filter(s => s.status === "PUBLISHED").map(s => (
                <label key={s.id} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedServices.includes(s.id)}
                    onChange={e => setSelectedServices(prev =>
                      e.target.checked ? [...prev, s.id] : prev.filter(id => id !== s.id)
                    )}
                  />
                  <span style={{ color: C.charcoal }}>{s.service_name}</span>
                  <span style={{ color: C.slate }}>{s.service_code}</span>
                </label>
              ))}
              {services.filter(s => s.status === "PUBLISHED").length === 0 && (
                <div className="text-xs" style={{ color: C.slate }}>No published services yet</div>
              )}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="create-package-submit-btn" className="flex-1" style={{ background: C.gold }}
              onClick={handleCreate} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Package
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Add Price Version Modal ───────────────────────────────────────────────────
function AddPriceModal({ serviceId, onClose, onSuccess }) {
  const [form, setForm] = useState({ base_price: "", currency: "INR", effective_from: new Date().toISOString().split("T")[0] });
  const [loading, setLoading] = useState(false);

  const handleAdd = async () => {
    if (!form.base_price) { toast.error("Base price required"); return; }
    setLoading(true);
    try {
      await serviceCatalogApi.addPriceVersion(serviceId, { ...form, base_price: parseFloat(form.base_price) });
      toast.success("Price version added");
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add price");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Add Price Version</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Base Price *</Label>
            <Input data-testid="price-amount-input" type="number" className="mt-1 h-8 text-sm"
              value={form.base_price} onChange={e => setForm(p => ({ ...p, base_price: e.target.value }))} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Currency</Label>
              <select className="mt-1 w-full border rounded-md px-3 py-1.5 text-sm h-8"
                value={form.currency} onChange={e => setForm(p => ({ ...p, currency: e.target.value }))}>
                {["INR","USD","AED","EUR","GBP"].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Effective From</Label>
              <Input type="date" className="mt-1 h-8 text-sm"
                value={form.effective_from} onChange={e => setForm(p => ({ ...p, effective_from: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button data-testid="add-price-submit-btn" className="flex-1" style={{ background: C.gold }} onClick={handleAdd} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Add Price
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ServiceCatalog() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("services");
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("PUBLISHED");
  const [showCreateService, setShowCreateService] = useState(false);
  const [showCreatePackage, setShowCreatePackage] = useState(false);
  const [showAddPrice, setShowAddPrice] = useState(null);
  const [selectedService, setSelectedService] = useState(null);

  const canManage  = hasCapability(user, "catalog.manage");
  const canPublish = hasCapability(user, "catalog.publish");
  const canRead    = hasCapability(user, "catalog.read");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: filterStatus || undefined, search: search || undefined };
      const [sRes, pRes] = await Promise.all([
        serviceCatalogApi.listServices(params),
        serviceCatalogApi.listPackages({ status: filterStatus || undefined }),
      ]);
      setServices(sRes.data?.items || []);
      setPackages(pRes.data || []);
    } catch {
      toast.error("Failed to load catalog");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!canRead) return (
    <div className="p-8 text-center" style={{ color: C.slate }}>
      You don't have permission to view the service catalog.
    </div>
  );

  const handlePublishService = async (sid) => {
    try {
      await serviceCatalogApi.publishService(sid);
      toast.success("Service published");
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const handleArchiveService = async (sid) => {
    try {
      await serviceCatalogApi.archiveService(sid);
      toast.success("Service archived");
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const handlePublishPackage = async (pid) => {
    try {
      await serviceCatalogApi.publishPackage(pid);
      toast.success("Package published");
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="service-catalog-page" className="min-h-screen p-6" style={{ background: C.ivory }}>
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold" style={{ color: C.charcoal }}>Service Catalog</h1>
            <p className="text-sm mt-0.5" style={{ color: C.slate }}>
              {canManage ? "Manage services and packages" : "Browse published services and packages"}
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchData} className="p-1.5 rounded hover:bg-black/5">
              <RefreshCw className="w-4 h-4" style={{ color: C.slate }} />
            </button>
            {canManage && (
              <>
                <Button
                  data-testid="create-service-btn"
                  size="sm"
                  style={{ background: C.gold, color: "#fff" }}
                  onClick={() => setShowCreateService(true)}
                  className="text-xs"
                >
                  <Plus className="w-3 h-3 mr-1" /> New Service
                </Button>
                <Button
                  data-testid="create-package-btn"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowCreatePackage(true)}
                  className="text-xs"
                >
                  <Package2 className="w-3 h-3 mr-1" /> New Package
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {["services", "packages"].map(t => (
            <button
              key={t}
              data-testid={`catalog-tab-${t}`}
              onClick={() => setActiveTab(t)}
              className="px-4 py-1.5 text-sm rounded-full transition-all capitalize"
              style={{
                background: activeTab === t ? C.gold : "transparent",
                color: activeTab === t ? "#fff" : C.slate,
                border: `1px solid ${activeTab === t ? C.gold : C.divider}`,
              }}
            >
              {t === "services" ? `Services (${services.length})` : `Packages (${packages.length})`}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.slate }} />
            <Input
              data-testid="catalog-search-input"
              className="pl-9 h-9"
              placeholder={`Search ${activeTab}...`}
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="catalog-status-filter"
            className="border rounded-md px-3 py-2 text-sm h-9"
            style={{ borderColor: C.divider, color: C.slate }}
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-32" style={{ color: C.slate }}>
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading...
          </div>
        ) : activeTab === "services" ? (
          <div className="space-y-2">
            {services.length === 0 ? (
              <div className="text-sm text-center py-12" style={{ color: C.slate }}>
                {filterStatus === "PUBLISHED" ? "No published services yet" : "No services found"}
                {canManage && (
                  <button
                    className="block mx-auto mt-2 text-xs underline"
                    style={{ color: C.gold }}
                    onClick={() => setShowCreateService(true)}
                  >
                    Create your first service
                  </button>
                )}
              </div>
            ) : (
              services.map(svc => (
                <div
                  key={svc.id}
                  data-testid={`service-card-${svc.id}`}
                  className="rounded-xl p-4 flex items-start gap-4"
                  style={{ background: C.sand, border: `1px solid ${C.divider}` }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm" style={{ color: C.charcoal }}>{svc.service_name}</span>
                      <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: C.ivory, color: C.slate }}>{svc.service_code}</span>
                      <StatusBadge status={svc.status} />
                    </div>
                    {svc.description && (
                      <div className="text-xs mt-0.5 text-truncate" style={{ color: C.slate }}>{svc.description}</div>
                    )}
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-xs" style={{ color: C.slate }}>Unit: {svc.unit}</span>
                      <span className="text-xs" style={{ color: C.slate }}>{svc.pricing_basis?.replace(/_/g, " ")}</span>
                      {svc.service_category_name && (
                        <span className="text-xs" style={{ color: C.slate }}>Category: {svc.service_category_name}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {svc.current_price != null && (
                      <div className="text-sm font-semibold" style={{ color: C.charcoal }}>
                        {fmtCurrency(svc.current_price, svc.currency)}
                      </div>
                    )}
                    {svc.price_versions?.length > 0 && (
                      <div className="text-xs mt-0.5" style={{ color: C.slate }}>
                        {svc.price_versions.length} price version{svc.price_versions.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                  {/* Actions */}
                  {canManage && (
                    <div className="flex flex-col gap-1 shrink-0">
                      {svc.status === "DRAFT" && canPublish && (
                        <button
                          data-testid={`publish-service-btn-${svc.id}`}
                          className="text-xs px-2 py-1 rounded text-green-700 hover:bg-green-50 border border-green-200"
                          onClick={() => handlePublishService(svc.id)}
                        >
                          Publish
                        </button>
                      )}
                      {svc.status === "PUBLISHED" && (
                        <button
                          data-testid={`add-price-btn-${svc.id}`}
                          className="text-xs px-2 py-1 rounded hover:bg-black/5 border"
                          style={{ color: C.slate, borderColor: C.divider }}
                          onClick={() => setShowAddPrice(svc.id)}
                        >
                          + Price
                        </button>
                      )}
                      {svc.status !== "ARCHIVED" && svc.status !== "RETIRED" && (
                        <button
                          data-testid={`archive-service-btn-${svc.id}`}
                          className="text-xs px-2 py-1 rounded text-red-600 hover:bg-red-50 border border-red-100"
                          onClick={() => handleArchiveService(svc.id)}
                        >
                          Archive
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          // Packages tab
          <div className="space-y-2">
            {packages.length === 0 ? (
              <div className="text-sm text-center py-12" style={{ color: C.slate }}>
                No packages found
                {canManage && (
                  <button
                    className="block mx-auto mt-2 text-xs underline"
                    style={{ color: C.gold }}
                    onClick={() => setShowCreatePackage(true)}
                  >
                    Create a package bundle
                  </button>
                )}
              </div>
            ) : (
              packages.map(pkg => (
                <div
                  key={pkg.id}
                  data-testid={`package-card-${pkg.id}`}
                  className="rounded-xl p-4"
                  style={{ background: C.sand, border: `1px solid ${C.divider}` }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Package2 className="w-4 h-4" style={{ color: C.gold }} />
                        <span className="font-medium text-sm" style={{ color: C.charcoal }}>{pkg.package_name}</span>
                        <span className="text-xs font-mono px-1.5 py-0.5 rounded" style={{ background: C.ivory, color: C.slate }}>{pkg.package_code}</span>
                        <StatusBadge status={pkg.status} />
                      </div>
                      {pkg.description && (
                        <div className="text-xs mt-0.5" style={{ color: C.slate }}>{pkg.description}</div>
                      )}
                      {(pkg.items || []).length > 0 && (
                        <div className="text-xs mt-1.5" style={{ color: C.slate }}>
                          Includes: {pkg.items.map(i => i.service_name || i.service_id).join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      {pkg.package_price && (
                        <div className="text-sm font-semibold" style={{ color: C.charcoal }}>
                          {fmtCurrency(pkg.package_price, pkg.currency)}
                        </div>
                      )}
                      {canManage && canPublish && pkg.status === "DRAFT" && (
                        <button
                          data-testid={`publish-package-btn-${pkg.id}`}
                          className="text-xs px-2 py-1 rounded text-green-700 hover:bg-green-50 border border-green-200"
                          onClick={() => handlePublishPackage(pkg.id)}
                        >
                          Publish
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {showCreateService && (
        <CreateServiceModal onClose={() => setShowCreateService(false)} onSuccess={fetchData} />
      )}
      {showCreatePackage && (
        <CreatePackageModal services={services} onClose={() => setShowCreatePackage(false)} onSuccess={fetchData} />
      )}
      {showAddPrice && (
        <AddPriceModal serviceId={showAddPrice} onClose={() => setShowAddPrice(null)} onSuccess={fetchData} />
      )}
    </div>
  );
}
